import React, { useState, useCallback, useEffect } from 'react';
import { crmImportAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription } from './ui/alert';
import {
  Upload, ArrowRight, ArrowLeft, Loader2, CheckCircle, XCircle,
  AlertTriangle, FileText, RotateCcw, Download, Save, BookTemplate,
  UserCheck, ClipboardList, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_SYSTEMS = [
  { value: 'generic', label: 'Generic CSV' },
  { value: 'hcp', label: 'Housecall Pro' },
  { value: 'servicetitan', label: 'ServiceTitan' },
  { value: 'jobber', label: 'Jobber' },
  { value: 'markate', label: 'Markate' },
  { value: 'fieldpulse', label: 'FieldPulse' },
];

const CUSTOMER_FIELDS = [
  { value: '_skip', label: '-- Skip --' },
  { value: 'display_name', label: 'Display Name' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'secondary_phone', label: 'Alt Phone' },
  { value: 'street', label: 'Street' },
  { value: 'service_unit', label: 'Unit / Line 2' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'Zip' },
  { value: 'company', label: 'Company' },
  { value: 'notes', label: 'Notes' },
  { value: 'source_id', label: 'Source ID' },
  { value: 'customer_type', label: 'Type' },
  { value: 'tags', label: 'Tags' },
  { value: 'lead_source', label: 'Lead Source' },
];

const JOB_FIELDS = [
  { value: '_skip', label: '-- Skip --' },
  { value: 'job_number', label: 'Job Number' },
  { value: 'job_name', label: 'Job Name' },
  { value: 'customer_name', label: 'Customer Name' },
  { value: '_customer_email', label: 'Customer Email (matching)' },
  { value: '_customer_phone', label: 'Customer Phone (matching)' },
  { value: '_customer_source_id', label: 'Customer ID (matching)' },
  { value: 'street', label: 'Street' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'Zip' },
  { value: 'job_type', label: 'Job Type' },
  { value: 'status', label: 'Status' },
  { value: 'scheduled_date', label: 'Scheduled Date' },
  { value: 'technician', label: 'Technician' },
  { value: 'description', label: 'Description' },
  { value: 'amount', label: 'Amount' },
  { value: 'tags', label: 'Tags' },
  { value: 'priority', label: 'Priority' },
  { value: 'completed_at', label: 'Completed Date' },
  { value: 'source_id', label: 'Source ID' },
  { value: '_location', label: 'Location (helper)' },
];

const STATUS_COLORS = {
  new: 'bg-green-100 text-green-800',
  duplicate: 'bg-gray-100 text-gray-600',
  needs_review: 'bg-amber-100 text-amber-800',
  error: 'bg-red-100 text-red-800',
  skipped: 'bg-slate-100 text-slate-500',
};

const STEPS = ['upload', 'mapping', 'preview', 'review', 'executing', 'summary'];

function StepIndicator({ current }) {
  const labels = ['Upload', 'Map Fields', 'Preview', 'Review', 'Import', 'Summary'];
  const idx = STEPS.indexOf(current);
  return (
    <div className="flex items-center gap-1 mb-4" data-testid="step-indicator">
      {labels.map((label, i) => (
        <React.Fragment key={label}>
          {i > 0 && <div className={`h-px w-4 ${i <= idx ? 'bg-blue-400' : 'bg-gray-200'}`} />}
          <div className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
            i === idx ? 'bg-blue-600 text-white font-medium' :
            i < idx ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
          }`}>{label}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

export function CRMImportModal({ open, onClose, entityType, onImportComplete }) {
  const [step, setStep] = useState('upload');
  const [loading, setLoading] = useState(false);
  const [sourceSystem, setSourceSystem] = useState('generic');
  const [sessionId, setSessionId] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  // V2 state
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [reviewResolutions, setReviewResolutions] = useState({});
  const [summary, setSummary] = useState(null);
  const [previewActions, setPreviewActions] = useState({}); // Track resolve actions for duplicate rows
  const [expandedDiffs, setExpandedDiffs] = useState({}); // Track expanded diff views

  const fieldOptions = entityType === 'customers' ? CUSTOMER_FIELDS : JOB_FIELDS;

  const reset = () => {
    setStep('upload');
    setLoading(false);
    setSourceSystem('generic');
    setSessionId(null);
    setHeaders([]);
    setMapping({});
    setPreview(null);
    setResult(null);
    setFileName('');
    setTemplates([]);
    setTemplateName('');
    setShowSaveTemplate(false);
    setReviewData(null);
    setReviewResolutions({});
    setSummary(null);
    setPreviewActions({});
    setExpandedDiffs({});
  };

  const handleClose = () => { reset(); onClose(); };

  // Load templates when opening mapping step
  const loadTemplates = useCallback(async () => {
    try {
      const res = await crmImportAPI.listTemplates(entityType);
      setTemplates(res.data.templates || []);
    } catch { /* non-critical */ }
  }, [entityType]);

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setLoading(true);
    setFileName(file.name);
    try {
      const res = await crmImportAPI.upload(file, entityType, sourceSystem);
      const data = res.data;
      setSessionId(data.session_id);
      setHeaders(data.headers);
      setMapping(data.suggested_mapping);

      // Load templates and auto-apply matching preset
      const tRes = await crmImportAPI.listTemplates(entityType);
      const allTemplates = tRes.data.templates || [];
      setTemplates(allTemplates);

      // Determine effective CRM: use detected_crm or selected sourceSystem
      const effectiveCrm = data.detected_crm || sourceSystem;
      if (data.detected_crm && data.detected_crm !== sourceSystem) {
        setSourceSystem(data.detected_crm);
        const crmLabel = SOURCE_SYSTEMS.find(s => s.value === data.detected_crm)?.label || data.detected_crm;
        toast.info(`Detected ${crmLabel} export format`);
      }

      // Auto-apply matching preset template
      const preset = allTemplates.find(
        t => t.is_global && t.crm_name === effectiveCrm && t.entity_type === entityType
      );
      if (preset) {
        const mj = preset.mapping_json || {};
        const newMapping = {};
        for (const h of data.headers) {
          if (mj[h]) newMapping[h] = mj[h];
          else if (data.suggested_mapping[h]) newMapping[h] = data.suggested_mapping[h];
        }
        setMapping(newMapping);
      }

      setStep('mapping');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  }, [entityType, sourceSystem]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFileUpload(e.dataTransfer.files[0]);
  };

  const applyTemplate = (t) => {
    const mj = t.mapping_json || {};
    // Apply template mappings to current headers
    const newMapping = {};
    for (const h of headers) {
      if (mj[h]) newMapping[h] = mj[h];
      else if (mapping[h]) newMapping[h] = mapping[h];
    }
    setMapping(newMapping);
    toast.success(`Template "${t.template_name}" applied`);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) { toast.error('Enter a template name'); return; }
    const cleanMapping = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v && v !== '_skip'));
    try {
      await crmImportAPI.saveTemplate({
        template_name: templateName.trim(),
        entity_type: entityType,
        crm_name: sourceSystem,
        mapping_json: cleanMapping,
      });
      toast.success('Template saved');
      setShowSaveTemplate(false);
      setTemplateName('');
      loadTemplates();
    } catch { toast.error('Failed to save template'); }
  };

  const handleConfirmMapping = async () => {
    setLoading(true);
    try {
      const cleanMapping = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v && v !== '_skip'));
      await crmImportAPI.confirmMapping(sessionId, cleanMapping);
      const res = await crmImportAPI.preview(sessionId);
      setPreview(res.data);
      setStep('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewResolve = async (rowId, action, matchId) => {
    try {
      await crmImportAPI.resolveRow(sessionId, rowId, action, matchId);
      setPreviewActions(prev => ({ ...prev, [rowId]: action }));
    } catch {
      toast.error('Failed to resolve row');
    }
  };

  const handleBatchResolve = async (action) => {
    try {
      const res = await crmImportAPI.batchResolve(sessionId, action, 'duplicate');
      toast.success(`${res.data.resolved_count} rows set to ${action === 'update_existing' ? 'Update' : action === 'skip' ? 'Skip' : 'Create New'}`);
      const newActions = { ...previewActions };
      (preview?.rows || []).filter(r => r.match_status === 'duplicate').forEach(r => { newActions[r.id] = action; });
      setPreviewActions(newActions);
    } catch {
      toast.error('Failed to batch resolve');
    }
  };

  const handleGoToReview = async () => {
    setLoading(true);
    try {
      const res = await crmImportAPI.getReviewRows(sessionId);
      setReviewData(res.data);
      // Init resolutions from server state
      const init = {};
      for (const row of (res.data.rows || [])) {
        if (row.resolved_action) {
          init[row.id] = { action: row.resolved_action, matchId: row.resolved_match_id };
        }
      }
      setReviewResolutions(init);
      setStep('review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load review rows');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveRow = async (rowId, action, matchId = null) => {
    setReviewResolutions(prev => ({ ...prev, [rowId]: { action, matchId } }));
    try {
      await crmImportAPI.resolveRow(sessionId, rowId, action, matchId);
    } catch { toast.error('Failed to save resolution'); }
  };

  const allReviewResolved = reviewData ? reviewData.rows.every(r => reviewResolutions[r.id]?.action) : true;

  const handleExecute = async () => {
    setStep('executing');
    setLoading(true);
    try {
      const res = await crmImportAPI.execute(sessionId);
      setResult(res.data);
      // Load summary
      const sumRes = await crmImportAPI.getSummary(sessionId);
      setSummary(sumRes.data);
      setStep('summary');
      if (onImportComplete) onImportComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
      setStep(reviewData?.total_review > 0 ? 'review' : 'preview');
    } finally {
      setLoading(false);
    }
  };

  const mappedCount = Object.values(mapping).filter(v => v && v !== '_skip').length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="crm-import-modal">
        <DialogHeader>
          <DialogTitle data-testid="import-modal-title">
            Import {entityType === 'customers' ? 'Customers' : 'Jobs'}
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Upload a CSV file to begin.'}
            {step === 'mapping' && 'Map CSV columns to fields. Use a template or customize.'}
            {step === 'preview' && 'Review matches before importing.'}
            {step === 'review' && 'Resolve ambiguous matches.'}
            {step === 'executing' && 'Importing records...'}
            {step === 'summary' && 'Import complete.'}
          </DialogDescription>
        </DialogHeader>

        <StepIndicator current={step} />

        {/* ===== STEP 1: Upload ===== */}
        {step === 'upload' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Source System</Label>
              <Select value={sourceSystem} onValueChange={setSourceSystem}>
                <SelectTrigger data-testid="source-system-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCE_SYSTEMS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
              onDragOver={e => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById('csv-upload-input').click()}
              data-testid="csv-drop-zone"
            >
              <input id="csv-upload-input" type="file" accept=".csv" className="hidden"
                onChange={e => handleFileUpload(e.target.files[0])} />
              {loading ? <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-500" /> : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">Drop a CSV file here or click to browse</p>
                  <p className="text-xs text-gray-400 mt-1">Supports HCP, ServiceTitan, Jobber, FieldPulse, and generic CSV</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* ===== STEP 2: Field Mapping ===== */}
        {step === 'mapping' && (
          <div className="space-y-4">
            {/* Template selector */}
            {templates.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="template-selector">
                <BookTemplate className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-500">Templates:</span>
                {templates.map(t => (
                  <Button key={t.id} variant="outline" size="sm" className="text-xs h-7"
                    onClick={() => applyTemplate(t)} data-testid={`template-${t.id}`}>
                    {t.template_name}
                    {t.is_global && <Badge variant="secondary" className="ml-1 text-[10px] px-1">Preset</Badge>}
                  </Button>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                <FileText className="w-4 h-4 inline mr-1" />{fileName} — {mappedCount}/{headers.length} mapped
              </p>
              <Button variant="ghost" size="sm" onClick={() => setShowSaveTemplate(!showSaveTemplate)}
                data-testid="save-template-toggle">
                <Save className="w-3 h-3 mr-1" /> Save Template
              </Button>
            </div>

            {showSaveTemplate && (
              <div className="flex items-center gap-2 p-2 border rounded-lg bg-gray-50" data-testid="save-template-form">
                <Input value={templateName} onChange={e => setTemplateName(e.target.value)}
                  placeholder="Template name..." className="h-8 text-sm" data-testid="template-name-input" />
                <Button size="sm" className="h-8" onClick={handleSaveTemplate} data-testid="save-template-btn">Save</Button>
              </div>
            )}

            <div className="border rounded-lg overflow-hidden max-h-[35vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/2">CSV Column</TableHead>
                    <TableHead className="w-1/2">Map To</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {headers.map(header => (
                    <TableRow key={header}>
                      <TableCell className="font-mono text-sm">{header}</TableCell>
                      <TableCell>
                        <Select value={mapping[header] || ''} onValueChange={v => setMapping(p => ({ ...p, [header]: v }))}>
                          <SelectTrigger className="h-8" data-testid={`map-${header.replace(/\s+/g, '-').toLowerCase()}`}>
                            <SelectValue placeholder="Skip" />
                          </SelectTrigger>
                          <SelectContent>
                            {fieldOptions.map(f => (
                              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Will Import / Will Skip Summary */}
            {(() => {
              const willImport = headers.filter(h => mapping[h] && mapping[h] !== '_skip');
              const willSkip = headers.filter(h => !mapping[h] || mapping[h] === '_skip');
              const getLabel = (h) => {
                const f = fieldOptions.find(fo => fo.value === mapping[h]);
                return f ? f.label : mapping[h];
              };
              return (
                <div className="grid grid-cols-2 gap-3" data-testid="mapping-summary">
                  <div className="border rounded-lg p-3 bg-green-50/50">
                    <p className="text-xs font-semibold text-green-700 mb-1.5" data-testid="mapped-count">
                      Will Import: {willImport.length} field{willImport.length !== 1 ? 's' : ''}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {willImport.map(h => (
                        <Badge key={h} variant="outline" className="text-[10px] bg-green-100 text-green-800 border-green-200">
                          {h} &rarr; {getLabel(h)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="border rounded-lg p-3 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-500 mb-1.5" data-testid="unmapped-count">
                      Will Skip: {willSkip.length} field{willSkip.length !== 1 ? 's' : ''}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {willSkip.map(h => (
                        <Badge key={h} variant="outline" className="text-[10px] bg-gray-100 text-gray-500 border-gray-200">
                          {h}
                        </Badge>
                      ))}
                      {willSkip.length === 0 && <span className="text-xs text-gray-400 italic">All fields mapped</span>}
                    </div>
                  </div>
                </div>
              );
            })()}

            <DialogFooter>
              <Button variant="outline" onClick={() => { reset(); }} data-testid="back-to-upload-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Start Over
              </Button>
              <Button onClick={handleConfirmMapping} disabled={loading || mappedCount === 0} data-testid="preview-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1" />}
                Preview Import
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ===== STEP 3: Preview ===== */}
        {step === 'preview' && preview && (() => {
          const dupCount = preview.rows.filter(r => r.match_status === 'duplicate').length;
          const updateCount = Object.values(previewActions).filter(a => a === 'update_existing').length;
          const skipOverrideCount = Object.values(previewActions).filter(a => a === 'skip').length;
          const createOverrideCount = Object.values(previewActions).filter(a => a === 'create_new').length;
          const importableCount = preview.new_count + updateCount + createOverrideCount;
          return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="preview-summary">
              <div className="border rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-600" data-testid="new-count">{preview.new_count + createOverrideCount}</p>
                <p className="text-xs text-gray-500">New</p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-600" data-testid="update-count">{updateCount}</p>
                <p className="text-xs text-gray-500">Will Update</p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-gray-500" data-testid="dup-count">{dupCount - updateCount - skipOverrideCount - createOverrideCount}</p>
                <p className="text-xs text-gray-500">Duplicates</p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-600" data-testid="review-count">{preview.review_count}</p>
                <p className="text-xs text-gray-500">Needs Review</p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-600" data-testid="error-count">{preview.error_count}</p>
                <p className="text-xs text-gray-500">Errors</p>
              </div>
            </div>

            {dupCount > 0 && (
              <Alert className="border-blue-200 bg-blue-50">
                <AlertTriangle className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-sm flex items-center justify-between">
                  <span>{dupCount} duplicate(s) found. Choose how to handle them:</span>
                  <span className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleBatchResolve('update_existing')} data-testid="batch-update-btn">
                      Update All
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleBatchResolve('skip')} data-testid="batch-skip-btn">
                      Skip All
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleBatchResolve('create_new')} data-testid="batch-create-btn">
                      Create All New
                    </Button>
                  </span>
                </AlertDescription>
              </Alert>
            )}

            {preview.review_count > 0 && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-sm">
                  {preview.review_count} row(s) need manual review before import can proceed.
                </AlertDescription>
              </Alert>
            )}

            <div className="border rounded-lg overflow-hidden max-h-[35vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Key Data</TableHead>
                    <TableHead className="w-28">Status</TableHead>
                    <TableHead>Match Details</TableHead>
                    <TableHead className="w-40">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.slice(0, 50).map(row => {
                    const isDup = row.match_status === 'duplicate';
                    const action = previewActions[row.id];
                    const mp = row.matched_record_preview;
                    const diff = row.field_diff || [];
                    const isDiffOpen = expandedDiffs[row.id];
                    const showDiffToggle = action === 'update_existing' && isDup;
                    return (
                    <React.Fragment key={row.id}>
                    <TableRow data-testid={`preview-row-${row.row_number}`}>
                      <TableCell className="text-xs text-gray-400">{row.row_number}</TableCell>
                      <TableCell className="text-sm">
                        <div>
                          {entityType === 'customers'
                            ? row.mapped_data?.display_name || row.mapped_data?.email || '-'
                            : row.mapped_data?.job_number || '-'}
                        </div>
                        {isDup && mp && !showDiffToggle && (
                          <div className="mt-1 text-xs bg-gray-50 rounded p-1.5 border" data-testid={`match-preview-${row.row_number}`}>
                            <p className="font-medium text-gray-600">Existing match:</p>
                            {entityType === 'customers' ? (
                              <>
                                {mp.display_name && <p>{mp.display_name}</p>}
                                {mp.phone && <p className="text-gray-400">Phone: {mp.phone}</p>}
                                {mp.email && <p className="text-gray-400">Email: {mp.email}</p>}
                              </>
                            ) : (
                              <>
                                {mp.job_number && <p>Job #{mp.job_number}</p>}
                                {mp.job_name && <p className="text-gray-400">{mp.job_name}</p>}
                                {mp.status && <p className="text-gray-400">Status: {mp.status}</p>}
                              </>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${
                          action === 'update_existing' ? 'bg-blue-100 text-blue-700' :
                          action === 'skip' ? 'bg-gray-100 text-gray-500' :
                          action === 'create_new' ? 'bg-green-100 text-green-700' :
                          STATUS_COLORS[row.match_status] || ''
                        }`}>
                          {action === 'update_existing' ? 'Update' :
                           action === 'skip' ? 'Skip' :
                           action === 'create_new' ? 'Create New' :
                           (<>{row.match_status === 'new' && <CheckCircle className="w-3 h-3 mr-1" />}
                             {row.match_status === 'duplicate' && <XCircle className="w-3 h-3 mr-1" />}
                             {row.match_status === 'needs_review' && <AlertTriangle className="w-3 h-3 mr-1" />}
                             {row.match_status}</>)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 max-w-[200px] truncate">
                        {row.match_details || row.error_message || ''}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1.5">
                        {isDup && (
                          <Select value={action || '_unresolved'} onValueChange={(val) => {
                            if (val === '_unresolved') return;
                            handlePreviewResolve(row.id, val, row.matched_record_id);
                          }}>
                            <SelectTrigger className="h-7 text-xs" data-testid={`action-select-${row.row_number}`}>
                              <SelectValue placeholder="Choose..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_unresolved" disabled>Choose...</SelectItem>
                              <SelectItem value="update_existing">Update Existing</SelectItem>
                              <SelectItem value="create_new">Create New</SelectItem>
                              <SelectItem value="skip">Skip</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        {showDiffToggle && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs w-full text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-1"
                            onClick={() => setExpandedDiffs(prev => ({ ...prev, [row.id]: !prev[row.id] }))}
                            data-testid={`view-changes-btn-${row.row_number}`}
                          >
                            {isDiffOpen ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                            {isDiffOpen ? 'Hide' : 'View'} Changes
                            {diff.length > 0 && <Badge variant="secondary" className="ml-1 text-[10px] px-1 h-4">{diff.length}</Badge>}
                          </Button>
                        )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {showDiffToggle && isDiffOpen && (
                      <TableRow data-testid={`diff-row-${row.row_number}`} className="bg-blue-50/30">
                        <TableCell colSpan={5} className="p-0">
                          <div className="px-4 py-2 border-t border-blue-100">
                            {diff.length === 0 ? (
                              <p className="text-xs text-gray-500 italic py-1" data-testid={`no-changes-${row.row_number}`}>
                                No changes detected — incoming data matches existing record.
                              </p>
                            ) : (
                              <div className="space-y-1" data-testid={`diff-details-${row.row_number}`}>
                                <p className="text-xs font-semibold text-blue-700 mb-1.5">
                                  {diff.length} field{diff.length !== 1 ? 's' : ''} will change:
                                </p>
                                <div className="grid gap-1">
                                  {diff.map(d => (
                                    <div key={d.field} className="flex items-start gap-2 text-xs bg-white rounded border px-2.5 py-1.5" data-testid={`diff-field-${d.field}`}>
                                      <span className="font-medium text-gray-700 w-24 shrink-0">{d.label}</span>
                                      <span className="text-red-600 line-through break-all">{d.old_value || '(empty)'}</span>
                                      <ArrowRight className="w-3 h-3 text-gray-400 shrink-0 mt-0.5" />
                                      <span className="text-green-700 font-medium break-all">{d.new_value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('mapping')} data-testid="back-to-mapping-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Remap
              </Button>
              {preview.review_count > 0 ? (
                <Button onClick={handleGoToReview} disabled={loading} data-testid="go-to-review-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserCheck className="w-4 h-4 mr-1" />}
                  Review {preview.review_count} Match{preview.review_count > 1 ? 'es' : ''}
                </Button>
              ) : (
                <Button onClick={handleExecute} disabled={importableCount === 0} data-testid="execute-import-btn">
                  <Download className="w-4 h-4 mr-1" />
                  Import {importableCount} Record{importableCount !== 1 ? 's' : ''}
                </Button>
              )}
            </DialogFooter>
          </div>
          );
        })()}

        {/* ===== STEP 4: Review ===== */}
        {step === 'review' && reviewData && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">
                {reviewData.total_review} row{reviewData.total_review > 1 ? 's' : ''} need review
              </p>
              <Badge className={allReviewResolved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                {Object.keys(reviewResolutions).length}/{reviewData.total_review} resolved
              </Badge>
            </div>

            <div className="space-y-3 max-h-[50vh] overflow-y-auto" data-testid="review-queue">
              {reviewData.rows.map(row => {
                const resolution = reviewResolutions[row.id];
                const mapped = row.mapped_data || {};
                const keyLabel = entityType === 'customers'
                  ? (mapped.display_name || mapped.email || `Row ${row.row_number}`)
                  : (mapped.job_number || `Row ${row.row_number}`);

                return (
                  <div key={row.id} className={`border rounded-lg p-3 ${resolution?.action ? 'border-green-200 bg-green-50/30' : 'border-amber-200'}`}
                    data-testid={`review-row-${row.row_number}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-sm">{keyLabel}</p>
                        {mapped.street && <p className="text-xs text-gray-500">{mapped.street}{mapped.city ? `, ${mapped.city}` : ''}</p>}
                        {mapped.email && <p className="text-xs text-gray-500">{mapped.email}</p>}
                      </div>
                      <Badge className="text-xs bg-amber-100 text-amber-700">
                        conf: {row.confidence_score}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">{row.match_details}</p>

                    <RadioGroup
                      value={resolution?.action || ''}
                      onValueChange={val => {
                        if (val === 'create_new' || val === 'skip') handleResolveRow(row.id, val);
                      }}
                      className="space-y-1"
                    >
                      {row.possible_matches.map(match => (
                        <div key={match.id} className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleResolveRow(row.id, 'link', match.id)}>
                          <RadioGroupItem value={`link_${match.id}`}
                            checked={resolution?.action === 'link' && resolution?.matchId === match.id} />
                          <label className="text-sm cursor-pointer flex-1">
                            <span className="font-medium">{match.display_name}</span>
                            {match.city && <span className="text-gray-400"> - {match.city}</span>}
                            {match.street && <span className="text-gray-400 text-xs ml-1">({match.street})</span>}
                            {match.email && <span className="text-gray-400 text-xs ml-1">{match.email}</span>}
                          </label>
                        </div>
                      ))}
                      <div className="flex items-center space-x-2 p-1.5 rounded hover:bg-green-50 cursor-pointer"
                        onClick={() => handleResolveRow(row.id, 'create_new')}>
                        <RadioGroupItem value="create_new" checked={resolution?.action === 'create_new'} />
                        <label className="text-sm cursor-pointer text-green-700 font-medium">Create New Record</label>
                      </div>
                      <div className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleResolveRow(row.id, 'skip')}>
                        <RadioGroupItem value="skip" checked={resolution?.action === 'skip'} />
                        <label className="text-sm cursor-pointer text-gray-500">Skip This Row</label>
                      </div>
                    </RadioGroup>
                  </div>
                );
              })}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('preview')} data-testid="back-to-preview-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={handleExecute} disabled={!allReviewResolved || loading} data-testid="execute-after-review-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Execute Import
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ===== STEP 5: Executing ===== */}
        {step === 'executing' && (
          <div className="flex flex-col items-center py-8">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
            <p className="text-sm text-gray-600">Importing records...</p>
          </div>
        )}

        {/* ===== STEP 6: Summary ===== */}
        {step === 'summary' && summary && (
          <div className="space-y-4">
            <div className="flex flex-col items-center py-2">
              <CheckCircle className="w-12 h-12 text-green-500 mb-2" />
              <p className="text-lg font-semibold">Import Complete</p>
              <p className="text-xs text-gray-400">{summary.file_name}</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center" data-testid="import-summary">
              <div className="border rounded-lg p-3">
                <p className="text-2xl font-bold text-green-600" data-testid="summary-created">{summary.records_created}</p>
                <p className="text-xs text-gray-500">Created</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-2xl font-bold text-blue-600" data-testid="summary-updated">{summary.records_updated || 0}</p>
                <p className="text-xs text-gray-500">Updated</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-2xl font-bold text-gray-500" data-testid="summary-skipped">{summary.duplicates_skipped + summary.user_skipped}</p>
                <p className="text-xs text-gray-500">Skipped</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-2xl font-bold text-purple-600" data-testid="summary-reviewed">{summary.review_resolved}</p>
                <p className="text-xs text-gray-500">Reviewed</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-2xl font-bold text-red-600" data-testid="summary-errors">{summary.errors}</p>
                <p className="text-xs text-gray-500">Errors</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-2xl font-bold text-gray-700">{summary.total_rows}</p>
                <p className="text-xs text-gray-500">Total Rows</p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClose} data-testid="import-done-btn">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// Import History + Rollback modal
// =========================================================================

export function ImportHistoryModal({ open, onClose, entityType, onRollbackComplete }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);
  const [detailSession, setDetailSession] = useState(null);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    if (open) loadSessions();
  }, [open, entityType]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const res = await crmImportAPI.sessions(entityType);
      setSessions(res.data.sessions || []);
    } catch { toast.error('Failed to load import history'); }
    finally { setLoading(false); }
  };

  const handleRollback = async (sessionId) => {
    if (!window.confirm('Are you sure? This will delete created records and restore updated records to their pre-import state.')) return;
    setRollingBack(sessionId);
    try {
      const res = await crmImportAPI.rollback(sessionId);
      toast.success(`Rolled back: ${res.data.deleted_count} deleted, ${res.data.blocked_count} blocked`);
      loadSessions();
      if (onRollbackComplete) onRollbackComplete();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rollback failed'); }
    finally { setRollingBack(null); }
  };

  const viewDetails = async (sessionId) => {
    try {
      const res = await crmImportAPI.getSummary(sessionId);
      setDetailData(res.data);
      setDetailSession(sessionId);
    } catch { toast.error('Failed to load details'); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[75vh] overflow-y-auto" data-testid="import-history-modal">
        <DialogHeader>
          <DialogTitle>Import History</DialogTitle>
          <DialogDescription>View past imports, details, and rollback if needed.</DialogDescription>
        </DialogHeader>

        {/* Detail view */}
        {detailSession && detailData && (
          <div className="space-y-3 mb-4 p-3 border rounded-lg bg-gray-50" data-testid="session-detail">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{detailData.file_name}</p>
              <Button variant="ghost" size="sm" onClick={() => { setDetailSession(null); setDetailData(null); }}>
                Close
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div><p className="font-bold text-green-600">{detailData.records_created}</p><p className="text-xs text-gray-400">Created</p></div>
              <div><p className="font-bold text-blue-600">{detailData.records_linked}</p><p className="text-xs text-gray-400">Linked</p></div>
              <div><p className="font-bold text-gray-500">{detailData.duplicates_skipped}</p><p className="text-xs text-gray-400">Duplicates Skipped</p></div>
              <div><p className="font-bold text-purple-600">{detailData.review_resolved}</p><p className="text-xs text-gray-400">Reviewed</p></div>
              <div><p className="font-bold text-red-600">{detailData.errors}</p><p className="text-xs text-gray-400">Errors</p></div>
              <div><p className="font-bold">{detailData.total_rows}</p><p className="text-xs text-gray-400">Total</p></div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No imports yet.</p>
        ) : (
          <div className="space-y-2">
            {sessions.map(s => (
              <div key={s.id} className="border rounded-lg p-3 flex items-center justify-between" data-testid={`session-${s.id}`}>
                <div>
                  <p className="text-sm font-medium">{s.file_name || 'Unknown file'}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-xs">{s.source_system}</Badge>
                    <Badge className={`text-xs ${
                      s.status === 'completed' ? 'bg-green-100 text-green-800' :
                      s.status === 'rolled_back' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-600'}`}>{s.status}</Badge>
                    <span className="text-xs text-gray-400">
                      {s.imported_count} imported / {s.total_rows} rows
                    </span>
                    {s.review_count > 0 && (
                      <span className="text-xs text-amber-500">{s.review_count} reviewed</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {(s.status === 'completed' || s.status === 'rolled_back') && (
                    <Button variant="ghost" size="sm" onClick={() => viewDetails(s.id)} data-testid={`detail-${s.id}`}>
                      <ClipboardList className="w-3 h-3 mr-1" /> Details
                    </Button>
                  )}
                  {s.status === 'completed' && (
                    <Button variant="outline" size="sm" onClick={() => handleRollback(s.id)}
                      disabled={rollingBack === s.id} data-testid={`rollback-${s.id}`}>
                      {rollingBack === s.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                      Undo
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
